import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Box, Grid, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import CustomTable from "../../shared/table/CustomTable";
import TableHeader from "../../shared/table/TableHeader";
import { getBankLogoURL, searchSwiftMessages } from "../state/actions";
import { useReactToPrint } from "react-to-print";
import PrintingScreen from "./PrintingScreen";

import { SwiftContext } from "../../shared/Swift/Swift";
import ViewMessages from "./ViewMessages";
import MailModal from "../SwiftDay/MailModal";
import { createPortal } from "react-dom";

const columnDefaults = {
  filterable: true,
  sortable: true,
  flex: 1,
  minWidth: 200,
};

/**
 * Required:
 
   MT Number: messageType
   UETR: uetr
   Transaction Date: transactionDate
   Session Number: sessionNumber
   Sequence Number: sequenceNumber
   Reference No.: referenceNumber
   Related Reference: relatedReference
   Sender: senderBIC
   Receiver: receiverBIC

 * Optional:
   
   Currency: currency
   Amount: amount
   Value Date: valueDate
   Message Status: sessionNumber
   MUR: mur
   GPI Status: referenceNumber
   Customer In: customerIn
   Customer Out: customerOut
   */

const defaultColumns = [
  {
    ...columnDefaults,
    field: "messageType",
    headerName: "Message Type",
  },
  {
    ...columnDefaults,
    field: "uetr",
    headerName: "UETR",
    renderCell: (params) => (
      <Tooltip title={params.value}>
        <span className="table-cell-trucate">{params.value}</span>
      </Tooltip>
    ),
  },
  {
    ...columnDefaults,
    field: "transactionDate",
    headerName: "Date",
  },
  {
    ...columnDefaults,
    field: "sendingDateTime",
    headerName: "Sending Date",
    valueGetter: (params) => params.row?.messageDetails.header.sendingDateTime,
  },
  {
    ...columnDefaults,
    field: "sessionNumber",
    headerName: "Session N°",
  },
  {
    ...columnDefaults,
    field: "sequenceNumber",
    headerName: "Seq N°",
  },
  {
    ...columnDefaults,
    field: "referenceNumber",
    headerName: "Reference N°",
  },
  {
    ...columnDefaults,
    field: "relatedReference",
    headerName: "Related Ref",
  },
  {
    ...columnDefaults,
    field: "senderBIC",
    headerName: "Sender",
  },
  {
    ...columnDefaults,
    field: "receiverBIC",
    headerName: "Receiver",
  },
  {
    ...columnDefaults,
    field: "messageStatus",
    headerName: "Message Status",
    valueGetter: (params) => {
      return params?.value?.header?.messageStatus;
    },
  },
  {
    ...columnDefaults,
    field: "exchangeRate",
    headerName: "Exchange Rate",
    sortable: true,
  },
  {
    ...columnDefaults,
    field: "clearanceCode",
    headerName: "Clearance Code",
  },
  {
    ...columnDefaults,
    field: "gpiStatus",
    headerName: "GPI Status",
  },
  {
    ...columnDefaults,
    field: "messageSource",
    headerName: "Message Source",
  },
  {
    ...columnDefaults,
    field: "intermediaryBank",
    headerName: "Intermediary Bank",
  },
  {
    ...columnDefaults,
    field: "correspondentSender",
    headerName: "Sender's Correspondent",
  },
  {
    ...columnDefaults,
    field: "correspondentReceiver",
    headerName: "Receiver's Correspondent",
  },
  {
    ...columnDefaults,
    field: "orderInstituation",
    headerName: "Ordering Institution",
  },
  {
    ...columnDefaults,
    field: "accountInstituation",
    headerName: "Account with Institution",
  },
];

const allColumns = [
  ...defaultColumns,
  {
    ...columnDefaults,
    field: "currency",
    headerName: "Currency",
  },
  {
    ...columnDefaults,
    field: "amount",
    headerName: "Amount",
  },
  {
    ...columnDefaults,
    field: "valueDate",
    headerName: "Value Date",
  },
  {
    ...columnDefaults,
    field: "mur",
    headerName: "MUR",
  },
  {
    ...columnDefaults,
    field: "customerIn",
    headerName: "Customer In",
  },
  {
    ...columnDefaults,
    field: "customerOut",
    headerName: "Customer Out",
  },
];

const persistColumns = (columns) => {
  localStorage.setItem(
    "swiftMessagesColumns",
    JSON.stringify(columns.map((c) => c.field))
  );
};

const loadPersistentColumns = () => {
  const persistentColumns = JSON.parse(
    localStorage.getItem("swiftMessagesColumns")
  );
  if (persistentColumns) {
    return allColumns
      .filter((c) => persistentColumns.includes(c.field))
      .sort(
        (a, b) =>
          persistentColumns.indexOf(a.field) -
          persistentColumns.indexOf(b.field)
      );
  }
  return defaultColumns;
};

const MessagesTable = ({ error, filters, success }) => {
  const dispatch = useDispatch();
  const rows = useSelector((state) => state.swiftMessages.rows);
  const updatedAt = useSelector((state) => state.swiftMessages.updatedAt);
  const processing = useSelector((state) => state.swiftMessages.processing);
  const pageSize = useSelector((state) => state.swiftMessages.pageSize);
  const pageNumber = useSelector((state) => state.swiftMessages.pageNumber);
  const totalItems = useSelector((state) => state.swiftMessages.totalItems);
  const totalPages = useSelector((state) => state.swiftMessages.totalPages);
  const sendingMail = useSelector((state) => state.swiftDay.sendingMail);

  const persistentColumns = loadPersistentColumns();
  const [tableRef, setTableRef] = useState(null);
  const [showPrint, togglePrint] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [viewRows, setViewRows] = useState([]);
  const [columns, setColumns] = useState(persistentColumns);
  const [isMailModalOpen, setIsMailModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const printingRef = useRef(null);
  const onBeforeGetContentResolve = useRef(null);
  const customizationRef = useRef(null);

  const { setExportBtnDisabled, openExportDialog, setOpenExportDialog } =
    useContext(SwiftContext);

  const onSelectionChange = (rows) => {
    setSelectedRows(rows);
    setExportBtnDisabled(rows.length === 0);
  };

  const handleColumnsChange = (items) => {
    setColumns([...items]);
    persistColumns(items);
  };

  const handlePaginationChange = (paginationFilter) => {
    dispatch(
      searchSwiftMessages(
        {
          searchCriteria: { ...filters },
          pageNumber,
          pageSize,
          ...paginationFilter,
        },
        error
      )
    );
  };

  const handleOnBeforeGetContent = () => {
    return new Promise((resolve) => {
      togglePrint((show) => !show);
      onBeforeGetContentResolve.current = resolve;
    });
  };

  const handlePrint = useReactToPrint({
    content: () => printingRef.current,
    documentTitle: "Swift Messages",
    copyStyles: true,
    onBeforeGetContent: handleOnBeforeGetContent,
    onAfterPrint: () => togglePrint(false),
  });

  const handleColumnHide = (field) => {
    const filteredColumns = columns.filter((column) => column.field !== field);
    setColumns([...filteredColumns]);
    persistColumns(filteredColumns);
  };

  const handleShowColumns = () => {
    setAnchorEl(customizationRef.current);
  };

  const onRowClick = (row) => {
    setViewRows([row]);
    setIsViewModalOpen(true);
  };

  const selectedRowsWithDetails = rows
    .filter((row, index) => selectedRows.includes(`${row.uetr}-${index}`))
    .map((row, index) => ({
      ...row,
      id: `${row.uetr}-${index}`,
    }));

  const loadBankLogoURL = useCallback(
    () => dispatch(getBankLogoURL(error)),
    [dispatch, error]
  );

  useEffect(() => {
    if (showPrint) onBeforeGetContentResolve.current();
  }, [showPrint, onBeforeGetContentResolve]);

  useEffect(() => {
    if (openExportDialog) {
      handlePrint();
      setOpenExportDialog(false);
    }
  }, [openExportDialog, setOpenExportDialog]);

  useEffect(() => {
    loadBankLogoURL();
  }, [loadBankLogoURL]);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TableHeader
            defaultColumns={defaultColumns}
            allColumns={allColumns}
            inUseColumns={columns}
            numberOfRows={totalItems}
            numberOfSelectedRows={selectedRows.length}
            handleColumnsChange={handleColumnsChange}
            handlePrint={handlePrint}
            handleMail={() => setIsMailModalOpen(true)}
            handleViewMessages={() => {
              setViewRows(selectedRowsWithDetails);
              setIsViewModalOpen(true);
            }}
            handleUnselectAll={() => {
              setSelectedRows([]);
              setExportBtnDisabled(true);
            }}
            {...{ anchorEl, setAnchorEl, handleShowColumns }}
          />
        </Grid>

        <Grid item xs={12} ref={(newTableRef) => setTableRef(newTableRef)}>
          <CustomTable
            {...{
              pageSize,
              pageNumber,
              totalItems,
              totalPages,
              columns,
              tableRef,
              updatedAt,
              loading: processing,
              onSelectionChange,
              selectedRows,
              handleColumnHide,
              onRowClick,
              onPaginationChange: handlePaginationChange,
            }}
            rows={rows.map((row, index) => ({
              ...row,
              id: `${row.uetr}-${index}`,
            }))}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: "none" }}>
        <PrintingScreen
          ref={printingRef}
          columns={columns}
          rows={selectedRowsWithDetails}
        />
      </Box>

      {isMailModalOpen && (
        <MailModal
          handleClose={() => setIsMailModalOpen(false)}
          referenceNumbers={selectedRowsWithDetails.map(
            (row) => row.referenceNumber
          )}
          success={success}
          error={error}
          sendingMail={sendingMail}
        />
      )}

      {isViewModalOpen && (
        <ViewMessages
          open={isViewModalOpen}
          messages={viewRows}
          handleClose={() => setIsViewModalOpen(false)}
        />
      )}
    </>
  );
};
export default MessagesTable;
